// Perfil: dados do usuário, vínculos, senha, 2FA e unidades de exibição.

import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Alert, Badge, Button, Card, Field, Input, Modal, PageHeader, Select } from "../components/ui";
import { api, ApiError, ROLE_LABEL, type UnitPrefs } from "../lib/api";
import { isAdmin, useMe } from "../lib/session";
import { MfaEnroll } from "./auth";

export function ProfilePage() {
  const { data: me } = useMe();
  if (!me) return null;
  return (
    <>
      <PageHeader title="Perfil" />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-5">
          <h2 className="font-semibold">{me.user.name}</h2>
          <p className="text-sm text-muted">{me.user.email}</p>
          <h3 className="mt-5 mb-2 text-sm font-medium">Acessos</h3>
          <ul className="space-y-2">
            {me.memberships.length === 0 && <li className="text-sm text-muted">Nenhum vínculo.</li>}
            {me.memberships.map((m) => (
              <li key={m.id} className="flex flex-wrap items-center gap-2 text-sm">
                <Badge tone="brand">{ROLE_LABEL[m.role]}</Badge>
                <span className="text-muted">
                  {m.role === "platform_admin" ? "toda a plataforma" : [m.group?.name, m.site?.name, m.unit?.name].filter(Boolean).join(" › ")}
                </span>
              </li>
            ))}
          </ul>
        </Card>
        <PasswordCard />
        <MfaCard enabled={me.user.totpEnabled} required={isAdmin(me)} />
        <UnitsCard prefs={me.unitPrefs} />
      </div>
    </>
  );
}

function PasswordCard() {
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      await api.post("/api/me/password", { current, password });
      setMsg({ kind: "ok", text: "Senha alterada. As outras sessões abertas foram encerradas." });
      setCurrent("");
      setPassword("");
    } catch (err) {
      setMsg({ kind: "error", text: err instanceof ApiError ? err.message : "erro ao alterar a senha" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-5">
      <h2 className="mb-4 font-semibold">Senha</h2>
      <form onSubmit={submit} className="space-y-4">
        {msg && <Alert kind={msg.kind}>{msg.text}</Alert>}
        <Field label="Senha atual">
          {(id) => <Input id={id} type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />}
        </Field>
        <Field label="Nova senha" hint="Pelo menos 10 caracteres.">
          {(id) => <Input id={id} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={10} />}
        </Field>
        <Button type="submit" loading={busy}>
          Alterar senha
        </Button>
      </form>
    </Card>
  );
}

function MfaCard({ enabled, required }: { enabled: boolean; required: boolean }) {
  const qc = useQueryClient();
  const [enrolling, setEnrolling] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function disable() {
    setError(null);
    try {
      await api.post("/api/me/mfa/disable", { code });
      setDisabling(false);
      setCode("");
      await qc.invalidateQueries({ queryKey: ["me"] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "erro");
    }
  }

  return (
    <Card className="p-5">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="font-semibold">Verificação em duas etapas</h2>
        {enabled ? <Badge tone="ok">ativa</Badge> : <Badge tone="warn">inativa</Badge>}
      </div>
      <p className="text-sm text-muted">
        {enabled
          ? "O login pede o código do aplicativo autenticador além da senha."
          : "Ative para que o login peça também um código do seu celular."}
        {required && " Obrigatória para o seu perfil."}
      </p>
      <div className="mt-4">
        {!enabled && <Button onClick={() => setEnrolling(true)}>Ativar</Button>}
        {enabled && !required && (
          <Button variant="secondary" onClick={() => setDisabling(true)}>
            Desativar
          </Button>
        )}
        {enabled && required && <p className="text-xs text-muted">Trocou de celular? Peça a outro administrador da plataforma para zerar o seu 2FA.</p>}
      </div>

      <Modal open={enrolling} onClose={() => setEnrolling(false)} title="Ativar verificação em duas etapas">
        {enrolling && (
          <MfaEnroll
            onDone={async () => {
              setEnrolling(false);
              await qc.invalidateQueries({ queryKey: ["me"] });
            }}
          />
        )}
      </Modal>

      <Modal
        open={disabling}
        onClose={() => setDisabling(false)}
        title="Desativar verificação em duas etapas"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDisabling(false)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={disable} disabled={code.length !== 6}>
              Desativar
            </Button>
          </>
        }
      >
        {error && <Alert kind="error">{error}</Alert>}
        <Field label="Código atual do aplicativo">
          {(id) => (
            <Input id={id} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" />
          )}
        </Field>
      </Modal>
    </Card>
  );
}

// Mesmo conceito do UnitSettingsService do app: o banco guarda SI, o prefixo é exibição.
function UnitsCard({ prefs }: { prefs: UnitPrefs }) {
  const qc = useQueryClient();
  const [p, setP] = useState<UnitPrefs>({ voltage: "kV", currentArm: "A", currentFld: "A", power: "MW", decimals: 2, ...prefs });
  const [saved, setSaved] = useState(false);

  async function save() {
    await api.put("/api/me/unit-prefs", p);
    setSaved(true);
    await qc.invalidateQueries({ queryKey: ["me"] });
    setTimeout(() => setSaved(false), 2000);
  }

  const sel = <K extends keyof UnitPrefs>(key: K, label: string, options: string[]) => (
    <Field label={label}>
      {(id) => (
        <Select id={id} value={String(p[key])} onChange={(e) => setP({ ...p, [key]: key === "decimals" ? Number(e.target.value) : e.target.value })}>
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );

  return (
    <Card className="p-5">
      <h2 className="mb-1 font-semibold">Unidades de exibição</h2>
      <p className="mb-4 text-sm text-muted">Como as grandezas aparecem nos painéis e gráficos.</p>
      <div className="grid grid-cols-2 gap-4">
        {sel("voltage", "Tensão", ["V", "kV", "MV"])}
        {sel("power", "Potência", ["W", "kW", "MW"])}
        {sel("currentArm", "Corrente de armadura", ["A", "kA"])}
        {sel("currentFld", "Corrente de excitação", ["A", "kA"])}
        {sel("decimals", "Casas decimais", ["0", "1", "2", "3", "4"])}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button onClick={save}>Salvar</Button>
        {saved && <span className="text-sm text-ok">Salvo</span>}
      </div>
    </Card>
  );
}
